import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';

const SERVICE_TYPES = [
  'Wheel Bearing',
  'Brake Inspection',
  'Lights Check',
  'Chains',
  'Breakaway Cable',
  'Lug Torque',
  'Full Service',
  'Routine Check',
  'Tire Service',
  'Initial Inspection',
  'Other',
];

const INTERVAL_OPTIONS = [
  { label: '30 days (1 month)', days: 30 },
  { label: '60 days (2 months)', days: 60 },
  { label: '90 days (3 months)', days: 90 },
  { label: '180 days (6 months)', days: 180 },
  { label: '365 days (1 year)', days: 365 },
];

// NOTE: Postgres bigint columns (last_serviced_at, created_at, etc.) are
// returned by the `pg` driver as STRINGS, not numbers, to avoid 64-bit
// precision loss. If you do string + number with `+`, JS concatenates
// instead of adding ("1778630400" + 15552000 = "177863040015552000"),
// which is what produced the "2058599979589 days remaining" bug.
// Always coerce these values with Number() before arithmetic.
function daysUntilDue(lastServicedAt, intervalDays) {
  const last = Number(lastServicedAt);
  const interval = Number(intervalDays);
  if (!last || !interval) return null;
  const dueAt = last + interval * 86400;
  const now = Math.floor(Date.now() / 1000);
  return Math.ceil((dueAt - now) / 86400);
}

function formatDate(epochSeconds) {
  if (!epochSeconds) return '';
  return new Date(Number(epochSeconds) * 1000).toISOString().split('T')[0];
}

function dateToEpoch(dateStr) {
  if (!dateStr) return null;
  return Math.floor(new Date(dateStr).getTime() / 1000);
}

function MultiSelectDropdown({ options, selected, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (id) => {
    if (id === 'all') {
      onChange(selected.length === options.length ? [] : options.map(o => o.id));
    } else {
      onChange(selected.includes(id) ? selected.filter(s => s !== id) : [...selected, id]);
    }
  };

  const label = selected.length === 0
    ? placeholder
    : selected.length === options.length
    ? 'All trailers'
    : selected.length === 1
    ? options.find(o => o.id === selected[0])?.name
    : `${selected.length} trailers selected`;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', padding: '8px 10px', border: '1px solid var(--slate-200)',
          borderRadius: '6px', fontSize: '13px', background: 'white',
          color: selected.length ? 'var(--slate-800)' : 'var(--slate-400)',
          textAlign: 'left', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
        <span>{label}</span>
        <span style={{ fontSize: '10px', color: 'var(--slate-400)' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: 'white', border: '1px solid var(--slate-200)', borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '220px', overflowY: 'auto', marginTop: '4px',
        }}>
          <label style={{
            display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px',
            cursor: 'pointer', fontSize: '13px', fontWeight: '600', borderBottom: '1px solid var(--slate-100)',
            color: 'var(--slate-700)',
          }}>
            <input type="checkbox" checked={selected.length === options.length}
              onChange={() => toggle('all')} />
            Select all
          </label>
          {options.map(o => (
            <label key={o.id} style={{
              display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px',
              cursor: 'pointer', fontSize: '13px', color: 'var(--slate-700)',
              background: selected.includes(o.id) ? 'var(--slate-50)' : 'white',
            }}>
              <input type="checkbox" checked={selected.includes(o.id)}
                onChange={() => toggle(o.id)} />
              {o.name}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AlertsPanel({ subAccountId, trackers }) {
  const [alerts, setAlerts] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const [saving, setSaving] = useState(false);
  const [servicingKey, setServicingKey] = useState(null);
  const [form, setForm] = useState({
    tracker_ids: [],
    label: SERVICE_TYPES[0],
    alert_type: 'time',
    threshold_miles: '3000',
    baseline_miles: '',
    interval_days: 90,
    last_serviced_at: '',
    notify_email: '',
  });

  const manualTrailers = trackers.filter(t => t.manual);

  const selectedTrailers = form.tracker_ids.map(id => trackers.find(t => t.id === id)).filter(Boolean);
  const allSelectedHaveGps = selectedTrailers.length > 0 && selectedTrailers.every(t => t.linked_ridedog_id);
  const anySelectedHaveGps = selectedTrailers.some(t => t.linked_ridedog_id);

  // Get current odometer for the selected trailer (single selection only for GPS)
  const singleSelectedTracker = selectedTrailers.length === 1 ? selectedTrailers[0] : null;
  const currentOdometer = singleSelectedTracker?.total_miles || null;

  // When trailer selection changes:
  // 1. Force time-based if no GPS
  // 2. Auto-populate baseline with current odometer if GPS linked
  useEffect(() => {
    if (form.alert_type === 'miles' && !anySelectedHaveGps && selectedTrailers.length > 0) {
      setForm(f => ({ ...f, alert_type: 'time' }));
    }
    // Auto-populate baseline when a single GPS trailer is selected and baseline is empty
    if (allSelectedHaveGps && selectedTrailers.length === 1 && currentOdometer && !editingAlert) {
      setForm(f => ({ ...f, baseline_miles: String(Math.round(currentOdometer)) }));
    }
  }, [form.tracker_ids]);

  const loadData = async () => {
    try {
      const [alertData, histData] = await Promise.all([
        api.getAlerts(subAccountId),
        api.getAlertHistory(subAccountId),
      ]);
      setAlerts(alertData);
      setHistory(histData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [subAccountId]);

  const openNewForm = () => {
    setEditingAlert(null);
    setForm({
      tracker_ids: [],
      label: SERVICE_TYPES[0],
      alert_type: 'time',
      threshold_miles: '3000',
      baseline_miles: '',
      interval_days: 90,
      last_serviced_at: formatDate(Math.floor(Date.now() / 1000)),
      notify_email: '',
    });
    setShowForm(true);
  };

  const openEditForm = (alert) => {
    setEditingAlert(alert);
    setForm({
      tracker_ids: [alert.tracker_id],
      label: alert.label || SERVICE_TYPES[0],
      alert_type: alert.alert_type || 'miles',
      threshold_miles: String(alert.threshold_miles || 3000),
      baseline_miles: String(alert.baseline_miles || 0),
      interval_days: alert.interval_days || 90,
      last_serviced_at: formatDate(alert.last_serviced_at),
      notify_email: alert.notify_email,
    });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (form.tracker_ids.length === 0) { alert('Please select at least one trailer.'); return; }
    setSaving(true);
    try {
      await Promise.all(form.tracker_ids.map(tracker_id => {
        const tracker = trackers.find(t => t.id === tracker_id);
        return api.saveAlert(subAccountId, {
          tracker_id,
          tracker_name: tracker?.name || tracker_id,
          label: form.label,
          alert_type: form.alert_type,
          threshold_miles: form.alert_type === 'miles' ? parseFloat(form.threshold_miles) : null,
          baseline_miles: form.alert_type === 'miles' ? parseFloat(form.baseline_miles || 0) : null,
          interval_days: form.alert_type === 'time' ? parseInt(form.interval_days) : null,
          last_serviced_at: form.alert_type === 'time' ? dateToEpoch(form.last_serviced_at) : null,
          notify_email: form.notify_email,
        });
      }));
      setShowForm(false);
      await loadData();
    } catch (err) {
      alert('Failed to save alert: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (trackerId, label) => {
    if (!window.confirm('Remove this maintenance alert?')) return;
    await api.deleteAlert(subAccountId, trackerId, label).catch(() => {});
    await loadData();
  };

  const handleMarkServiced = async (alert) => {
    const alertKey = `${alert.tracker_id}-${alert.label}`;
    const tracker = trackers.find(t => t.id === alert.tracker_id);
    const currentMiles = tracker?.total_miles || 0;
    const now = Math.floor(Date.now() / 1000);

    const confirmMsg = alert.alert_type === 'time'
      ? `Mark "${alert.label}" as serviced on ${alert.tracker_name}?\n\nThis resets the interval. Next alert in ${alert.interval_days} days.`
      : `Mark "${alert.label}" as serviced on ${alert.tracker_name}?\n\nThis resets the odometer baseline to ${Math.round(currentMiles).toLocaleString()} miles. Next alert at ${Math.round(currentMiles + alert.threshold_miles).toLocaleString()} miles.`;

    if (!window.confirm(confirmMsg)) return;

    setServicingKey(alertKey);
    try {
      await api.resetAlert(subAccountId, alert.tracker_id, alert.label, currentMiles, now);
      await loadData();
    } catch (err) {
      alert('Failed to reset alert: ' + err.message);
    } finally {
      setServicingKey(null);
    }
  };

  const alertsByTrailer = alerts.reduce((acc, alert) => {
    const key = alert.tracker_id;
    if (!acc[key]) acc[key] = { name: alert.tracker_name, alerts: [] };
    acc[key].alerts.push(alert);
    return acc;
  }, {});

  // Calculate next alert odometer for display
  const nextAlertMiles = (baseline, threshold) => {
    const b = parseFloat(baseline || 0);
    const t = parseFloat(threshold || 0);
    return Math.round(b + t).toLocaleString();
  };

  if (loading) return <div className="loading-screen"><div className="spinner"/></div>;

  const inputStyle = { padding: '8px 10px', border: '1px solid var(--slate-200)', borderRadius: '6px', fontSize: '13px', width: '100%' };
  const hintStyle = { fontSize: '12px', color: 'var(--slate-400)', marginTop: '5px', lineHeight: '1.5' };

  return (
    <div className="alerts-panel">
      <div className="alerts-header">
        <div>
          <h2>Maintenance Alerts</h2>
          <p>Get an email when a trailer is due for service — by mileage or by time.</p>
        </div>
        <button className="btn-primary" onClick={openNewForm}>+ Add Alert</button>
      </div>

      {showForm && (
        <div className="alert-form-card">
          <h3>{editingAlert ? 'Edit Alert' : 'New Maintenance Alert'}</h3>
          <form onSubmit={handleSave}>

            <div className="form-row">
              <div className="form-group">
                <label>Trailers</label>
                {editingAlert ? (
                  <select disabled style={inputStyle}>
                    <option>{editingAlert.tracker_name}</option>
                  </select>
                ) : (
                  <MultiSelectDropdown
                    options={manualTrailers.map(t => ({ id: t.id, name: t.name }))}
                    selected={form.tracker_ids}
                    onChange={(ids) => setForm(f => ({ ...f, tracker_ids: ids }))}
                    placeholder="Select trailers..."
                  />
                )}
              </div>
              <div className="form-group">
                <label>Component</label>
                <select value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} required style={inputStyle}>
                  {SERVICE_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            {/* Alert type */}
            <div className="form-group">
              <label>Alert Type</label>
              <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
                {allSelectedHaveGps && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontWeight: 'normal' }}>
                    <input type="radio" name="alert_type" value="miles"
                      checked={form.alert_type === 'miles'}
                      onChange={() => setForm(f => ({ ...f, alert_type: 'miles' }))} />
                    By Mileage
                  </label>
                )}
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontWeight: 'normal' }}>
                  <input type="radio" name="alert_type" value="time"
                    checked={form.alert_type === 'time'}
                    onChange={() => setForm(f => ({ ...f, alert_type: 'time' }))} />
                  By Time
                </label>
              </div>
              {form.tracker_ids.length > 0 && !allSelectedHaveGps && (
                <p style={hintStyle}>Mileage-based alerts require a linked RideDog GPS tracker. These trailers can only use time-based alerts.</p>
              )}
            </div>

            {/* Miles fields */}
            {form.alert_type === 'miles' && (
              <div className="form-row">
                <div className="form-group">
                  <label>Service interval (miles)</label>
                  <input type="number" min="100" step="100" value={form.threshold_miles}
                    onChange={e => setForm(f => ({ ...f, threshold_miles: e.target.value }))}
                    placeholder="e.g. 3000" required style={inputStyle} />
                  <p style={hintStyle}>How many miles between each service. E.g. every 3,000 miles.</p>
                </div>
                <div className="form-group">
                  <label>
                    Odometer at last service
                    {currentOdometer && (
                      <span style={{ fontWeight: '400', color: 'var(--slate-400)', fontSize: '12px', marginLeft: '6px' }}>
                        — current reading: {Math.round(currentOdometer).toLocaleString()} mi
                      </span>
                    )}
                  </label>
                  <input type="number" min="0" value={form.baseline_miles}
                    onChange={e => setForm(f => ({ ...f, baseline_miles: e.target.value }))}
                    placeholder={currentOdometer ? String(Math.round(currentOdometer)) : '0'}
                    style={inputStyle} />
                  <p style={hintStyle}>
                    Enter the odometer reading from when you last serviced this component.
                    {form.baseline_miles && form.threshold_miles && (
                      <> Alert will fire at <strong>{nextAlertMiles(form.baseline_miles, form.threshold_miles)} mi</strong>.</>
                    )}
                  </p>
                </div>
              </div>
            )}

            {/* Time fields */}
            {form.alert_type === 'time' && (
              <div className="form-row">
                <div className="form-group">
                  <label>Service interval</label>
                  <select value={form.interval_days}
                    onChange={e => setForm(f => ({ ...f, interval_days: parseInt(e.target.value) }))}
                    required style={inputStyle}>
                    {INTERVAL_OPTIONS.map(o => <option key={o.days} value={o.days}>{o.label}</option>)}
                  </select>
                  <p style={hintStyle}>How often this component needs to be serviced.</p>
                </div>
                <div className="form-group">
                  <label>Date of last service</label>
                  <input type="date" value={form.last_serviced_at}
                    onChange={e => setForm(f => ({ ...f, last_serviced_at: e.target.value }))}
                    required style={inputStyle} />
                  <p style={hintStyle}>When was this component last serviced? Leave as today if just serviced.</p>
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Notify email</label>
                <input type="email" value={form.notify_email}
                  onChange={e => setForm(f => ({ ...f, notify_email: e.target.value }))}
                  placeholder="you@company.com" required style={inputStyle} />
                <p style={hintStyle}>We'll send an email here when service is due.</p>
              </div>
            </div>

            {!editingAlert && form.tracker_ids.length > 1 && (
              <div style={{ background: 'var(--slate-50)', border: '1px solid var(--slate-200)', borderRadius: '6px', padding: '10px 12px', marginBottom: '12px', fontSize: '13px', color: 'var(--slate-600)' }}>
                This will create <strong>{form.tracker_ids.length} alerts</strong> — one for each selected trailer.
              </div>
            )}

            <div className="form-actions">
              <button type="button" className="btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Saving…' : editingAlert ? 'Save Alert' : `Create ${form.tracker_ids.length > 1 ? form.tracker_ids.length + ' Alerts' : 'Alert'}`}
              </button>
            </div>
          </form>
        </div>
      )}

      {alerts.length === 0 && !showForm ? (
        <div className="empty-state">
          <span className="empty-icon">🔧</span>
          <h3>No maintenance alerts set up yet</h3>
          <p>Add an alert to get notified when a trailer is due for service.</p>
          <button className="btn-primary" onClick={openNewForm}>Add Your First Alert</button>
        </div>
      ) : (
        <div className="alerts-list">
          {Object.values(alertsByTrailer).map(group => (
            <div key={group.name} style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--slate-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', paddingBottom: '6px', borderBottom: '1px solid var(--slate-100)' }}>
                {group.name}
              </div>
              {group.alerts.map(alert => {
                const tracker = trackers.find(t => t.id === alert.tracker_id);
                const alertKey = `${alert.tracker_id}-${alert.label}`;
                const isServicing = servicingKey === alertKey;
                const isMiles = alert.alert_type === 'miles' || !alert.alert_type;
                const hasGps = !!(tracker?.linked_ridedog_id);

                const currentMiles = tracker?.total_miles || 0;
                const milesSinceService = hasGps
                  ? Math.max(0, currentMiles - (alert.baseline_miles || 0))
                  : parseFloat(alert.baseline_miles || 0);
                const milesPct = isMiles ? Math.min(100, (milesSinceService / alert.threshold_miles) * 100) : 0;

                const daysLeft = !isMiles ? daysUntilDue(alert.last_serviced_at, alert.interval_days) : null;
                const totalDays = Number(alert.interval_days) || 1;
                const daysElapsed = !isMiles ? (totalDays - (daysLeft || 0)) : 0;
                const timePct = !isMiles ? Math.min(100, (daysElapsed / totalDays) * 100) : 0;

                const pct = isMiles ? milesPct : timePct;
                const isDue = pct >= 100 || (daysLeft !== null && daysLeft <= 0);
                const isWarning = pct >= 80;

                const nextServiceAt = isMiles
                  ? Math.round((alert.baseline_miles || 0) + alert.threshold_miles).toLocaleString() + ' mi'
                  : null;

                return (
                  <div key={alertKey} className={`alert-row ${isDue ? 'due' : isWarning ? 'warning' : ''}`}>
                    <div className="alert-row-main">
                      <div className="alert-tracker-name">
                        {alert.label && <span className="alert-label-badge">{alert.label}</span>}
                        <span className="alert-label-badge" style={{ background: isMiles ? '#e0f0ff' : '#f0e0ff', color: isMiles ? '#0066cc' : '#6600cc' }}>
                          {isMiles ? 'Miles' : 'Time'}
                        </span>
                      </div>
                      <div className="alert-meta">
                        {isMiles
                          ? <>Every <strong>{Number(alert.threshold_miles).toLocaleString()} mi</strong>{nextServiceAt && <> · next service at <strong>{nextServiceAt}</strong></>}</>
                          : <>Every <strong>{alert.interval_days} days</strong></>
                        }
                        {' · '}notify <strong>{alert.notify_email}</strong>
                      </div>
                      <div className="alert-progress-wrap">
                        <div className="alert-progress-bar">
                          <div className={`alert-progress-fill ${isDue ? 'due' : isWarning ? 'warning' : ''}`}
                            style={{ width: `${pct}%` }} />
                        </div>
                        <span className="alert-progress-label">
                          {isMiles
                            ? <>{Math.round(milesSinceService).toLocaleString()} mi driven since last service &nbsp;·&nbsp; {Math.round(Math.max(0, alert.threshold_miles - milesSinceService)).toLocaleString()} mi remaining</>
                            : daysLeft !== null && daysLeft > 0
                              ? `${daysLeft} days remaining`
                              : daysLeft !== null
                              ? `${Math.abs(daysLeft)} days overdue`
                              : 'No date set'
                          }
                          {isDue && <span className="badge-due"> MAINTENANCE DUE</span>}
                          {!isDue && isWarning && <span className="badge-warning"> Soon</span>}
                        </span>
                      </div>
                      <div style={{ marginTop: '8px' }}>
                        <button
                          className={isDue ? 'btn-primary' : 'btn-ghost'}
                          style={{ fontSize: '0.8rem', padding: '4px 12px' }}
                          onClick={() => handleMarkServiced(alert)}
                          disabled={isServicing}>
                          {isServicing ? 'Saving…' : 'Mark as Serviced'}
                        </button>
                      </div>
                    </div>
                    <div className="alert-row-actions">
                      <button className="btn-icon" onClick={() => openEditForm(alert)} title="Edit">✏️</button>
                      <button className="btn-icon danger" onClick={() => handleDelete(alert.tracker_id, alert.label)} title="Delete">🗑</button>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {history.length > 0 && (
        <div className="alert-history">
          <h3>Alert History</h3>
          <table className="history-table">
            <thead>
              <tr>
                <th>Trailer</th>
                <th>Component</th>
                <th>Type</th>
                <th>Miles at alert</th>
                <th>Threshold</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {history.map(h => (
                <tr key={h.id}>
                  <td>{h.tracker_name}</td>
                  <td>{h.label || '—'}</td>
                  <td>{h.alert_type === 'time' ? 'Time' : 'Miles'}</td>
                  <td>{h.miles_at_alert ? Math.round(h.miles_at_alert).toLocaleString() + ' mi' : '—'}</td>
                  <td>{h.threshold_miles ? Number(h.threshold_miles).toLocaleString() + ' mi' : h.interval_days ? h.interval_days + ' days' : '—'}</td>
                  <td>{new Date(Number(h.alerted_at) * 1000).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
